/**
 * DailyReflectionPageV2 — redesigned Daily Inspirations shell, port
 * of the prototype's `.screen-inspirations` page-head
 * (cms-redesign-mockup.html ~7888–7898).
 *
 * The in-app tab description is edited inline via InAppDescriptionPanel
 * under the header (per CMS feedback, Monday #12171251310 — it used to
 * hide behind an "Update description" modal). Data flow matches v1:
 *   GET /daily-inspirations/description → `general_info_value` (legacy
 *   field name, inconsistent with the other intro endpoints which return
 *   the full general_settings object — so we GET locally here instead of
 *   leaning on the `general_data` reducer slice).
 *   PUT via the existing `updateDailyInspirationDescription` action.
 *
 * Header action: "+ New Inspiration" → coral primary, /daily-inspirations/create
 *
 * Body is DailyInspirationsTableV2 which carries its own toolbar
 * (month navigation + Calendar/List view toggle).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import DailyInspirationsTableV2 from './v2/DailyInspirationsTableV2';
import { apiV2 } from '../../../actions';
import { updateDailyInspirationDescription } from '../../../actions/dailyReflectionActions';
import { PageHeader, Button, InAppDescriptionPanel } from '../../redesign';

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

export default function DailyReflectionPageV2() {
  const dispatch = useDispatch();
  const [description, setDescription] = useState('');

  // True once a save has happened — a mount-time GET that resolves
  // after the save must not overwrite the saved value with stale text.
  const savedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    apiV2
      .get('/daily-inspirations/description')
      .then((resp) => {
        if (!cancelled && !savedRef.current) {
          setDescription(resp?.data?.general_info_value || '');
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSaveDescription = (value) => {
    // Baseline only moves once the PUT succeeds — on failure the panel
    // stays dirty so the admin can retry (or Discard back to the real
    // server value).
    return dispatch(
      updateDailyInspirationDescription(value, () => {
        savedRef.current = true;
        setDescription(value);
      }),
    );
  };

  // Count chip — derived from the same reducer slice the table reads.
  const total = useSelector((s) => {
    const arr = s.daily_reflections.daily_reflections;
    return Array.isArray(arr) ? arr.length : undefined;
  });
  const totalLabel = total != null ? `${total.toLocaleString()} items` : undefined;

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Content Library"
        title="Daily Inspirations"
        total={totalLabel}
        actions={
          <Link to="/daily-inspirations/create" className="no-underline">
            <Button variant="coral" leadingIcon={PlusSign}>
              New Inspiration
            </Button>
          </Link>
        }
      />
      <InAppDescriptionPanel
        tabName="Daily Inspirations"
        value={description}
        onSave={handleSaveDescription}
      />
      <DailyInspirationsTableV2 />
    </div>
  );
}
